import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'compact' | 'featured';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group flex gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
      >
        <div className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={movie.coverImage}
            alt={movie.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            title={`${movie.title} - 免费在线观看`}
          />
        </div>
        <div className="flex flex-col justify-between py-1 min-w-0">
          <div>
            <h4 className="font-heading text-foreground-100 text-sm font-semibold group-hover:text-primary-400 transition-colors truncate">
              {movie.title}
            </h4>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-accent-500 font-medium">{movie.rating}</span>
              <span className="text-xs text-foreground-500">{movie.year}</span>
              <span className="text-xs text-foreground-500">{movie.quality}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {movie.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="px-2 py-0.5 text-xs rounded bg-secondary-500/15 text-secondary-400">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group relative overflow-hidden rounded-xl cursor-pointer h-[420px] md:h-[500px]"
      >
        <img
          src={movie.coverImage}
          alt={movie.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={`${movie.title} - 免费在线观看`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 text-xs rounded-full bg-primary-500 text-foreground-50 font-medium">
              {movie.category}
            </span>
            <span className="text-xs text-foreground-300">{movie.quality}</span>
          </div>
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-2">
            {movie.title}
          </h3>
          <p className="text-foreground-300 text-sm line-clamp-2 max-w-lg">
            {movie.description}
          </p>
          <div className="flex items-center gap-3 mt-3">
            <span className="flex items-center gap-1 text-accent-400 text-sm">
              <i className="ri-star-fill text-xs"></i>
              {movie.rating}
            </span>
            <span className="text-foreground-400 text-sm">{movie.duration}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${movie.id}`}
      className="group cursor-pointer"
      data-product-shop
    >
      <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
        <img
          src={movie.coverImage}
          alt={movie.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
          title={`${movie.title} - 免费在线观看`}
        />
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {movie.isNew && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-primary-500 text-foreground-50 font-medium">
              新上线
            </span>
          )}
          {movie.isHot && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-accent-500 text-foreground-950 font-medium">
              热门
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-1.5 py-0.5 text-xs rounded bg-background-950/80 text-foreground-50 font-medium">
            {movie.quality}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background-950/90 to-transparent p-3 pt-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <p className="text-foreground-200 text-xs line-clamp-2">{movie.description}</p>
        </div>
      </div>
      <h4 className="font-heading text-foreground-100 text-sm font-semibold group-hover:text-primary-400 transition-colors duration-200 truncate">
        {movie.title}
      </h4>
      <div className="flex items-center gap-2 mt-1">
        <span className="flex items-center gap-0.5 text-accent-400 text-xs">
          <i className="ri-star-fill text-[10px]"></i>
          {movie.rating}
        </span>
        <span className="text-foreground-500 text-xs">{movie.year}</span>
        <span className="text-foreground-500 text-xs">{movie.region}</span>
      </div>
    </Link>
  );
}